<template>
  <div class="form-section">
    <h4 class="text-responsive-lg font-bold text-center mb-responsive text-ietextlight">
      Review and Submit Cancellation Request
    </h4>
    
    <div class="space-y-responsive">
      <!-- Review information section -->
      <div class="p-4 border border-gray-200 rounded-md mb-6">
        <h4 class="text-lg font-semibold mb-4 text-gray-800">Cancellation Details:</h4>
        <div class="space-y-2">
          <p><strong>Reason for Cancellation:</strong> {{ formatReason(reason) }}</p>
          
          <!-- Show realtor info if selling home -->
          <div v-if="reason === 'selling_home' && realtorInfo && (realtorInfo.firstName || realtorInfo.lastName || realtorInfo.email || realtorInfo.phoneNumber)" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Realtor/Lender Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p><strong>First Name:</strong> {{ realtorInfo.firstName }}</p>
              <p><strong>Last Name:</strong> {{ realtorInfo.lastName }}</p>
              <p><strong>Email:</strong> {{ realtorInfo.email }}</p>
              <p><strong>Phone:</strong> {{ realtorInfo.phoneNumber }}</p>
            </div>
          </div>
          
          <!-- New Insurer Info -->
          <div v-if="reason === 'new_insurer' && dynamicFields.newInsurer" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">New Insurer Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p><strong>New Insurer Name:</strong> {{ dynamicFields.newInsurer.name }}</p>
              <p><strong>New Policy Number:</strong> {{ dynamicFields.newInsurer.policyNumber }}</p>
              <p class="sm:col-span-2"><strong>Effective Date:</strong> {{ formatDate(dynamicFields.newInsurer.effectiveDate) }}</p>
            </div>
          </div>
          
          <!-- Ownership Change Info -->
          <div v-if="reason === 'ownership_change' && dynamicFields.ownershipChange" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Ownership Change Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p class="sm:col-span-2"><strong>New Owner Name:</strong> {{ dynamicFields.ownershipChange.newOwnerName }}</p>
              <p><strong>Transfer Date:</strong> {{ formatDate(dynamicFields.ownershipChange.transferDate) }}</p>
              <p><strong>Reason:</strong> {{ formatOwnershipReason(dynamicFields.ownershipChange.reason) }}</p>
            </div>
          </div>
          
          <!-- Risk Reduction Info -->
          <div v-if="reason === 'risk_reduction' && dynamicFields.riskReduction" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Risk Reduction Information:</h5>
            <p><strong>Date:</strong> {{ formatDate(dynamicFields.riskReduction.date) }}</p>
            <p><strong>Description:</strong> {{ dynamicFields.riskReduction.description }}</p>
          </div>
          
          <!-- Mortgage Change Info -->
          <div v-if="reason === 'mortgage_change' && dynamicFields.mortgageChange" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Mortgage Change Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p class="sm:col-span-2"><strong>Previous Mortgage Company:</strong> {{ dynamicFields.mortgageChange.previousCompany }}</p>
              <p><strong>Payoff Date:</strong> {{ formatDate(dynamicFields.mortgageChange.payoffDate) }}</p>
              <p><strong>Loan Number:</strong> {{ dynamicFields.mortgageChange.loanNumber }}</p>
            </div>
          </div>
          
          <!-- Moving Info -->
          <div v-if="reason === 'moving' && dynamicFields.moving" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Moving Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p class="sm:col-span-2"><strong>New Address:</strong> {{ dynamicFields.moving.newAddress }}</p>
              <p><strong>Moving Date:</strong> {{ formatDate(dynamicFields.moving.movingDate) }}</p>
              <p><strong>Property Sold:</strong> {{ dynamicFields.moving.isPropertySold ? 'Yes' : 'No' }}</p>
            </div>
          </div>
          
          <!-- Financial Hardship Info -->
          <div v-if="reason === 'financial' && dynamicFields.financial" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Financial Hardship Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p><strong>Hardship Type:</strong> {{ formatHardshipType(dynamicFields.financial.hardshipType) }}</p>
              <p><strong>Expected Duration:</strong> {{ formatDuration(dynamicFields.financial.expectedDuration) }}</p>
              <p class="sm:col-span-2"><strong>Alternative Coverage Needs:</strong> {{ dynamicFields.financial.alternativeCoverage }}</p>
            </div>
          </div>
          
          <!-- Consolidation Info -->
          <div v-if="reason === 'consolidation' && dynamicFields.consolidation" class="mt-4 p-4 bg-blue-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Policy Consolidation Information:</h5>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <p><strong>New Policy Number:</strong> {{ dynamicFields.consolidation.policyNumber }}</p>
              <p><strong>Insurance Provider:</strong> {{ dynamicFields.consolidation.provider }}</p>
              <p class="sm:col-span-2"><strong>Effective Date:</strong> {{ formatDate(dynamicFields.consolidation.effectiveDate) }}</p>
            </div>
          </div>
          
          <!-- Show callback time if win-back selected -->
          <div v-if="isWinBackSelected && callbackTime" class="mt-4 p-4 bg-green-50 rounded">
            <h5 class="font-semibold text-gray-700 mb-2">Win-Back Callback Time:</h5>
            <p><strong>Date:</strong> {{ formatDate(callbackTime.date) }}</p>
            <p><strong>Time:</strong> {{ formatTime(callbackTime.time) }}</p>
          </div>
        </div>
      </div>
      
      <!-- Additional comments -->
      <div class="form-field">
        <label class="block text-responsive-sm font-semibold text-gray-700 mb-2">
          Additional Comments
        </label>
        <textarea
          :value="comments"
          @input="$emit('update:comments', $event.target.value)"
          rows="3"
          class="w-full p-2 border border-gray-300 rounded-md"
          placeholder="Enter any additional information about your cancellation request"
        ></textarea>
        <p v-if="errors?.comments" class="text-red-500 text-xs italic">
          {{ errors.comments }}
        </p>
      </div>
      
      <!-- Terms and conditions -->
      <div class="mt-6 p-4 border border-gray-200 rounded-md bg-gray-50">
        <h5 class="font-semibold text-gray-800 mb-2">Terms and Conditions</h5>
        <p class="text-sm text-red-500 mb-4">Important: Please read the following terms and conditions carefully.</p>
        
        <div class="max-h-48 overflow-y-auto p-2 border border-gray-200 bg-white mb-4">
          <p class="text-sm text-gray-700 mb-4">
            By submitting this cancellation request form, you acknowledge that you are initiating a request to cancel your insurance
            policy with Insurance Express and its affiliated member companies. However, please note that submitting this form does
            not automatically cancel your policy. All cancellation requests are subject to review and approval by Insurance Express
            and its underwriting department.
          </p>
          
          <p class="text-sm text-gray-700 font-semibold mb-2">Terms and Conditions:</p>
          
          <ul class="list-disc pl-5 space-y-2 text-sm text-gray-700">
            <li>
              <strong>Request Submission:</strong> By submitting this form, you are requesting cancellation of your insurance policy.
              Your request will be processed in accordance with the terms and conditions outlined in your insurance policy and
              applicable state regulations.
            </li>
            <li>
              <strong>Pending Approval:</strong> Please be aware that your cancellation request is not considered final until it has
              been reviewed and approved by Insurance Express. You will receive confirmation of the cancellation once it has been
              processed.
            </li>
            <li>
              <strong>Policy Obligations:</strong> Until your cancellation request is approved and confirmed by Insurance Express, you
              are still obligated to fulfill all terms, conditions, and payments outlined in your insurance policy. Failure to do so
              may result in penalties, fees, or legal actions.
            </li>
            <li>
              <strong>Effective Date:</strong> The effective date of cancellation, if approved, will be determined by Insurance
              Express and may vary depending on policy terms and state regulations. Please refer to your policy documents for
              specific details.
            </li>
            <li>
              <strong>No Coverage:</strong> Once your policy is canceled, you will no longer have coverage under the insurance policy.
              It is your responsibility to secure alternative coverage if needed.
            </li>
          </ul>
        </div>
        
        <div class="mb-4">
          <label class="inline-flex items-center">
            <input
              type="checkbox"
              :checked="userAgrees"
              @change="$emit('update:userAgrees', !userAgrees)"
              :class="customerType === 'customer' ? 'enhanced-checkbox-customer' : 'enhanced-checkbox'"
            />
            <span class="ml-2 text-sm text-gray-700">
              I have read and agree to the terms and conditions.
            </span>
          </label>
          <p v-if="errors?.userAgrees" class="text-red-500 text-xs italic mt-1">
            {{ errors.userAgrees }}
          </p>
        </div>
      </div>
    </div>
    
    <!-- Navigation buttons -->
    <div class="flex-responsive-col justify-between mt-responsive">
      <button
        @click="$emit('previous')"
        class="btn-responsive-secondary mb-2 sm:mb-0"
      >
        Back
      </button>
      <button
        @click="$emit('submit')"
        :disabled="isSubmitting || !userAgrees"
        :class="[
          customerType === 'lender' ? 'btn-lender' : 'btn-customer', 
          {'opacity-50 cursor-not-allowed': isSubmitting || !userAgrees}
        ]"
      >
        {{ isSubmitting ? 'Submitting...' : 'Submit Cancellation Request' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsAndSubmitStep',
  props: {
    reason: {
      type: String,
      required: true
    },
    realtorInfo: {
      type: Object,
      default: () => ({})
    },
    isWinBackSelected: {
      type: Boolean,
      default: false
    },
    callbackTime: {
      type: Object,
      default: () => ({})
    },
    dynamicFields: {
      type: Object,
      default: () => ({})
    },
    comments: {
      type: String,
      default: ''
    },
    userAgrees: {
      type: Boolean,
      default: false
    },
    customerType: {
      type: String,
      default: 'customer',
      validator: value => ['customer', 'lender'].includes(value)
    },
    isSubmitting: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'update:comments',
    'update:userAgrees',
    'previous',
    'submit'
  ],
  methods: {
    formatReason(reasonValue) {
      const reasonsMap = {
        'selling_home': 'Selling the Home',
        'new_insurer': 'Switching to a New Insurer',
        'ownership_change': 'Change in Ownership',
        'risk_reduction': 'Significant Reduction in Risk',
        'mortgage_change': 'No Longer Required by Mortgage',
        'moving': 'Moving and Not Renting Out Your Old Home',
        'financial': 'Financial Hardship',
        'consolidation': 'Consolidation of Policies'
      };
      return reasonsMap[reasonValue] || reasonValue;
    },
    formatOwnershipReason(reason) {
      const reasonMap = {
        'sale': 'Property Sale',
        'gift': 'Gift/Transfer to Family',
        'inheritance': 'Inheritance',
        'legal': 'Legal Settlement',
        'other': 'Other'
      };
      return reasonMap[reason] || reason;
    },
    formatHardshipType(type) {
      const typeMap = {
        'job_loss': 'Job Loss',
        'medical': 'Medical Expenses',
        'disaster': 'Natural Disaster',
        'divorce': 'Divorce/Separation',
        'business': 'Business Downturn',
        'other': 'Other Hardship'
      };
      return typeMap[type] || type;
    },
    formatDuration(duration) {
      const durationMap = {
        'temporary': 'Temporary (1-3 months)',
        'short_term': 'Short-term (3-6 months)',
        'long_term': 'Long-term (6+ months)',
        'permanent': 'Permanent change',
        'unknown': 'Unknown'
      };
      return durationMap[duration] || duration;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    formatTime(timeValue) {
      const timeMap = {
        'morning': 'Morning (9am - 12pm)',
        'afternoon': 'Afternoon (12pm - 5pm)',
        'evening': 'Evening (5pm - 7pm)'
      };
      return timeMap[timeValue] || timeValue;
    }
  }
};
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}
</style> 